import { FC, useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { CalendarEventPayload, EditableCalendarEvent } from '../../services/calendar'
import { EventType, getEventTypeDescription } from '../../enum/provider/calendar/EventType'
import {
  EVENT_FREQUENCY_OPTIONS,
  EventFrequency,
  getEventFrequencyDescription,
} from '../../enum/provider/calendar/EventFrequency'

/** Erros de validação vindos do Laravel: campo => mensagens. */
export type FormErrors = Record<string, string[]>

interface CalendarEventFormModalProps {
  isOpen: boolean
  isSaving: boolean
  /** Evento sendo editado (relido do banco); nulo quando é criação. */
  event: EditableCalendarEvent | null
  /** Dia clicado na grade — vira a data inicial do formulário. */
  date: string
  errors: FormErrors
  onClose: () => void
  onSubmit: (payload: CalendarEventPayload, id?: number | null) => void
}

const DEFAULT_START = '08:00'
const DEFAULT_END = '09:00'

const CalendarEventFormModal: FC<CalendarEventFormModalProps> = ({
  isOpen,
  isSaving,
  event,
  date,
  errors,
  onClose,
  onSubmit,
}) => {
  const [title, setTitle] = useState('')
  const [type, setType] = useState<EventType>(EventType.BLOQUEIO)
  const [allDay, setAllDay] = useState(false)
  const [day, setDay] = useState(date)
  const [startsAt, setStartsAt] = useState(`${date}T${DEFAULT_START}`)
  const [endsAt, setEndsAt] = useState(`${date}T${DEFAULT_END}`)
  const [recurrence, setRecurrence] = useState<EventFrequency>(EventFrequency.DOES_NOT_REPEAT)
  const [recurrenceUntil, setRecurrenceUntil] = useState('')
  const [notes, setNotes] = useState('')

  // Reabrir o modal sempre recomeça do evento (edição) ou do dia clicado
  // (criação) — sem isto o formulário guardaria o preenchimento anterior.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (event) {
      setTitle(event.title)
      setType(
        event.type === EventType.COMPROMISSO_PESSOAL ? EventType.COMPROMISSO_PESSOAL : EventType.BLOQUEIO,
      )
      setAllDay(event.all_day)
      setDay(event.date)
      setStartsAt(event.starts_at)
      setEndsAt(event.ends_at)
      setRecurrence(event.recurrence_type)
      setRecurrenceUntil(event.recurrence_until ?? '')
      setNotes(event.notes ?? '')
      return
    }

    setTitle('')
    setType(EventType.BLOQUEIO)
    setAllDay(false)
    setDay(date)
    setStartsAt(`${date}T${DEFAULT_START}`)
    setEndsAt(`${date}T${DEFAULT_END}`)
    setRecurrence(EventFrequency.DOES_NOT_REPEAT)
    setRecurrenceUntil('')
    setNotes('')
  }, [isOpen, event, date])

  const handleSubmit = () => {
    const payload: CalendarEventPayload = {
      title,
      type,
      all_day: allDay,
      recurrence_type: recurrence,
      recurrence_until: recurrence === EventFrequency.DOES_NOT_REPEAT ? null : recurrenceUntil || null,
      notes: notes || null,
    }

    if (allDay) {
      payload.date = day
    } else {
      payload.starts_at = startsAt
      payload.ends_at = endsAt
    }

    onSubmit(payload, event?.id ?? null)
  }

  const error = (field: string) => errors[field]?.[0]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // Tela cheia no celular: com teclado aberto sobre um modal centrado, os
      // campos de horário ficavam atrás do teclado e o rodapé fora de alcance.
      size={{ base: 'full', sm: 'md' }}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" color="gray.700" borderBottomWidth="1px" borderColor="gray.100">
          {event ? 'Editar evento' : 'Novo bloqueio'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4} px={{ base: 4, sm: 6 }}>
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={2}>
              <Heading fontSize="md" color="gray.600">
                O que você quer registrar?
              </Heading>
              <Flex gap={2}>
                {[EventType.BLOQUEIO, EventType.COMPROMISSO_PESSOAL].map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setType(option)}
                    className={`flex-1 rounded-lg border-2 px-3 py-2.5 text-left transition ${
                      type === option
                        ? option === EventType.BLOQUEIO
                          ? 'border-red-400 bg-red-50'
                          : 'border-yellow-400 bg-yellow-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <span className="block text-sm font-medium text-slate-700">
                      {option === EventType.BLOQUEIO ? 'Bloqueio' : 'Compromisso'}
                    </span>
                    <span className="block text-[11px] text-slate-400">
                      {option === EventType.BLOQUEIO ? 'Indisponível' : 'Pessoal'}
                    </span>
                  </button>
                ))}
              </Flex>
              {error('type') && (
                <Text fontSize="sm" color="red.500">
                  {error('type')}
                </Text>
              )}
            </Flex>

            <Flex direction="column" gap={2}>
              <Heading fontSize="md" color="gray.600">
                Título
              </Heading>
              <Input
                value={title}
                onChange={changed(setTitle)}
                placeholder="Ex: Férias, Consulta médica, Almoço…"
                fontSize="sm"
                color="gray.600"
              />
              {error('title') && (
                <Text fontSize="sm" color="red.500">
                  {error('title')}
                </Text>
              )}
            </Flex>

            <Checkbox isChecked={allDay} onChange={e => setAllDay(e.target.checked)} color="gray.600">
              Dia inteiro
            </Checkbox>

            {allDay ? (
              <Flex direction="column" gap={2}>
                <Heading fontSize="md" color="gray.600">
                  Data
                </Heading>
                <Input type="date" value={day} onChange={changed(setDay)} fontSize="sm" color="gray.600" />
                {error('date') && (
                  <Text fontSize="sm" color="red.500">
                    {error('date')}
                  </Text>
                )}
              </Flex>
            ) : (
              // Empilhados de propósito: lado a lado, dois `datetime-local`
              // ficam estreitos demais para mostrar data e hora ao mesmo tempo.
              <Flex direction="column" gap={4}>
                <Flex direction="column" gap={2}>
                  <Heading fontSize="md" color="gray.600">
                    Início
                  </Heading>
                  <Input
                    type="datetime-local"
                    value={startsAt}
                    onChange={changed(setStartsAt)}
                    fontSize="sm"
                    color="gray.600"
                  />
                  {error('starts_at') && (
                    <Text fontSize="sm" color="red.500">
                      {error('starts_at')}
                    </Text>
                  )}
                </Flex>
                <Flex direction="column" gap={2}>
                  <Heading fontSize="md" color="gray.600">
                    Fim
                  </Heading>
                  <Input
                    type="datetime-local"
                    value={endsAt}
                    onChange={changed(setEndsAt)}
                    fontSize="sm"
                    color="gray.600"
                  />
                  {error('ends_at') && (
                    <Text fontSize="sm" color="red.500">
                      {error('ends_at')}
                    </Text>
                  )}
                </Flex>
              </Flex>
            )}

            <Flex direction={{ base: 'column', sm: 'row' }} gap={{ base: 4, sm: 3 }}>
              <Flex direction="column" gap={2} flex={1}>
                <Heading fontSize="md" color="gray.600">
                  Repetir
                </Heading>
                <Select
                  value={recurrence}
                  onChange={e => setRecurrence(Number(e.target.value))}
                  fontSize="sm"
                  color="gray.600"
                >
                  {EVENT_FREQUENCY_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {getEventFrequencyDescription(option)}
                    </option>
                  ))}
                </Select>
              </Flex>
              {recurrence !== EventFrequency.DOES_NOT_REPEAT && (
                <Flex direction="column" gap={2} flex={1}>
                  <Heading fontSize="md" color="gray.600">
                    Repetir até
                  </Heading>
                  <Input
                    type="date"
                    min={day}
                    value={recurrenceUntil}
                    onChange={changed(setRecurrenceUntil)}
                    fontSize="sm"
                    color="gray.600"
                  />
                  {error('recurrence_until') && (
                    <Text fontSize="sm" color="red.500">
                      {error('recurrence_until')}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>

            <Flex direction="column" gap={2}>
              <Heading fontSize="md" color="gray.600">
                Observações{' '}
                <Text as="span" fontSize="xs" color="gray.400">
                  (opcional)
                </Text>
              </Heading>
              <Textarea
                value={notes}
                onChange={changed(setNotes)}
                rows={2}
                placeholder={`Detalhes do ${getEventTypeDescription(type).toLowerCase()}`}
                _placeholder={{ color: 'gray.400' }}
              />
            </Flex>
          </Flex>
        </ModalBody>

        {/* No celular os dois botões dividem a largura e ficam na altura do
            polegar; no desktop voltam a ser um par compacto à direita. */}
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="gray.100">
          <Button
            size={{ base: 'md', sm: 'sm' }}
            variant="ghost"
            flex={{ base: 1, sm: 'none' }}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            size={{ base: 'md', sm: 'sm' }}
            colorScheme="blue"
            flex={{ base: 1, sm: 'none' }}
            isLoading={isSaving}
            onClick={handleSubmit}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function changed(setter: (value: string) => void) {
  return (event: { target: { value: string } }) => setter(event.target.value)
}

export default CalendarEventFormModal
